/* ============================================================
   RAD BAG SIZE FINDER

   Step 1  cassette size  (preset key like "14x17", or a custom W x H)
   Step 2  format         (cut / roll / handheld / ziplock, or all)
   Result  show the matching cards

   Every product card is already in the DOM, rendered by Liquid with
   data-fits, data-format and data-bag-w / data-bag-h. This file only
   decides which ones are visible, so pricing, the login gate and Bold's
   hooks are whatever the server rendered.

   No jQuery. Turbo 10 defers jQuery to the end of <body>, and this needs
   to run inside the theme editor and after InstantClick swaps too.
   ============================================================ */
(function () {
  'use strict';

  var FORMAT_LABELS = {
    cut: 'Individually Cut',
    roll: 'Roll',
    handheld: 'Handheld',
    ziplock: 'Zip Lock',
    other: 'Rad Bag®'
  };

  function num(value) {
    var n = parseFloat(value);
    return isNaN(n) ? null : n;
  }

  // "14 × 17 in" and '14" x 17" cassette' both become "14x17" (smaller side first).
  function dimsKey(text) {
    var m = String(text || '').match(/(\d+(?:\.\d+)?)[^\d×x]{0,4}[×x]\s*(\d+(?:\.\d+)?)/);
    if (!m) return '';
    var a = parseFloat(m[1]);
    var b = parseFloat(m[2]);
    return Math.min(a, b) + 'x' + Math.max(a, b);
  }

  var HASH_BASE = 'find-your-size';

  function SizeFinder(root) {
    this.root = root;
    this.clearance = num(root.dataset.clearance);
    if (this.clearance === null) this.clearance = 1;

    this.customLimit = num(root.dataset.customLimit);
    if (this.customLimit === null) this.customLimit = 6;

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    this.steps = {
      size: root.querySelector('[data-step="size"]'),
      format: root.querySelector('[data-step="format"]'),
      results: root.querySelector('[data-step="results"]')
    };
    this.summary = root.querySelector('[data-summary]');
    this.note = root.querySelector('[data-custom-note]');
    this.none = root.querySelector('[data-none]');
    this.perfectFit = root.querySelector('[data-perfect-fit]');
    this.customPanel = root.querySelector('[data-custom-panel]');
    this.customError = root.querySelector('[data-custom-error]');

    this.state = { sizeKey: null, sizeLabel: null, custom: null, format: null };

    this.bind();

    // Step 2 sits on screen, grayed out, until a size is chosen.
    this.waitFormat();

    // Restore a linked or bookmarked selection, e.g. #find-your-size/14x17/roll.
    if (location.hash.indexOf('#' + HASH_BASE + '/') === 0) this.restore(location.hash);
  }

  /* ---------- matching ---------- */

  // Preset buttons carry keys like "14x17". Returns { w, h } or null.
  function parseKey(key) {
    var parts = String(key || '').split('x');
    if (parts.length !== 2) return null;
    var w = num(parts[0]);
    var h = num(parts[1]);
    return w === null || h === null ? null : { w: w, h: h };
  }

  // A card matches a custom size when its bag clears the cassette by the
  // configured margin on both sides, in either orientation.
  SizeFinder.prototype.matchesCustom = function (card, w, h) {
    var bw = num(card.dataset.bagW);
    var bh = num(card.dataset.bagH);
    if (bw === null || bh === null) return false;

    var c = this.clearance;
    var upright = bw >= w + c && bh >= h + c;
    var turned = bw >= h + c && bh >= w + c;
    return upright || turned;
  };

  SizeFinder.prototype.bagArea = function (card) {
    var bw = num(card.dataset.bagW);
    var bh = num(card.dataset.bagH);
    return bw === null || bh === null ? Infinity : bw * bh;
  };

  SizeFinder.prototype.cardsForSize = function () {
    var self = this;

    // A preset button and a typed size use the same geometry, so clicking
    // 14 x 17 returns exactly what typing 14 x 17 under "Other size" does.
    // Plenty of bags are technically large enough (a 23x29 will swallow an
    // 8x10), so rank by finished bag size and keep the closest fits.
    var dims = this.state.custom || parseKey(this.state.sizeKey);
    if (!dims) return [];

    return this.cards
      .filter(function (card) {
        return self.matchesCustom(card, dims.w, dims.h);
      })
      .sort(function (a, b) {
        return self.bagArea(a) - self.bagArea(b);
      });
  };

  /* ---------- rendering ---------- */

  SizeFinder.prototype.showStep = function (name) {
    var el = this.steps[name];
    if (!el) return;
    el.hidden = false;
    el.classList.add('is-active');
  };

  SizeFinder.prototype.hideStep = function (name) {
    var el = this.steps[name];
    if (!el) return;
    el.hidden = true;
    el.classList.remove('is-active');
  };

  // Step 2 before a size is chosen: every format visible but grayed out, so
  // people can see the whole flow up front.
  SizeFinder.prototype.waitFormat = function () {
    var el = this.steps.format;
    if (!el) return;
    el.hidden = false;
    el.classList.remove('is-active');
    el.classList.add('is-waiting');
    Array.prototype.forEach.call(el.querySelectorAll('[data-format]'), function (b) {
      b.hidden = false;
      b.disabled = true;
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
  };

  SizeFinder.prototype.setPressed = function (buttons, active) {
    buttons.forEach(function (b) {
      var on = b === active;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  // Only offer formats that actually exist for the chosen size.
  SizeFinder.prototype.buildFormatStep = function () {
    var available = {};
    this.cardsForSize().forEach(function (card) {
      available[card.dataset.format] = true;
    });

    var buttons = Array.prototype.slice.call(
      this.steps.format.querySelectorAll('[data-format]')
    );
    var shown = 0;

    this.steps.format.classList.remove('is-waiting');
    buttons.forEach(function (b) { b.disabled = false; });

    buttons.forEach(function (b) {
      var f = b.dataset.format;
      if (f === '__all') return;
      var on = !!available[f];
      b.hidden = !on;
      if (on) shown++;
    });

    // "Show me all" is only meaningful when there is more than one format.
    var all = this.steps.format.querySelector('[data-format="__all"]');
    if (all) all.hidden = shown < 2;

    this.setPressed(buttons, null);
    return shown;
  };

  SizeFinder.prototype.renderResults = function () {
    var self = this;
    var matches = this.cardsForSize();

    if (this.state.format && this.state.format !== '__all') {
      matches = matches.filter(function (card) {
        return card.dataset.format === self.state.format;
      });
    }

    var total = matches.length;

    // Cap to the closest fits, the same for a preset button or a typed size.
    var capped = false;
    if (matches.length > this.customLimit) {
      matches = matches.slice(0, this.customLimit);
      capped = true;
    }

    // Most common fit for the chosen size goes first; otherwise closest fit first.
    var want = this.state.custom
      ? dimsKey(this.state.custom.w + 'x' + this.state.custom.h)
      : dimsKey(this.state.sizeKey);
    matches = matches.map(function (card, i) {
      card.__best = !!want && dimsKey(card.dataset.fit) === want;
      return { c: card, i: i, b: card.__best ? 0 : 1 };
    }).sort(function (a, b) { return (a.b - b.b) || (a.i - b.i); })
      .map(function (x) { return x.c; });

    this.cards.forEach(function (card) {
      var i = matches.indexOf(card);
      card.hidden = i === -1;
      // The grid is source-ordered; `order` reflects the ranking without
      // moving nodes around and losing focus.
      card.style.order = i === -1 ? '' : String(i);
      var flag = card.querySelector('[data-best]');
      var isBest = i !== -1 && !!card.__best;
      if (flag) flag.hidden = !isBest;
      card.classList.toggle('is-best', isBest);
    });

    var label = this.state.custom
      ? this.state.custom.w + ' × ' + this.state.custom.h + ' in'
      : this.state.sizeLabel;

    var shown = matches.length;
    var noun = shown === 1 ? 'Rad Bag®' : 'Rad Bags®';
    var format =
      this.state.format && this.state.format !== '__all'
        ? ' · ' + (FORMAT_LABELS[this.state.format] || this.state.format)
        : '';

    if (!shown) {
      this.summary.textContent = 'Contact us for your custom size.';
    } else {
      this.summary.textContent = capped
        ? 'Closest ' + shown + ' of ' + total + ' ' + noun + ' large enough for ' + label + format
        : shown + ' ' + noun + ' large enough for ' + label + format;
    }

    if (this.note) this.note.hidden = !(this.state.custom && shown > 0);
    if (this.none) this.none.hidden = shown > 0;

    // The custom-fit call to action belongs on both "nothing matched" and any
    // custom size, which is exactly when someone could not find their size.
    if (this.perfectFit) {
      this.perfectFit.hidden = !(shown === 0 || this.state.custom);
    }
    this.showStep('results');
  };

  /* ---------- flow ---------- */

  SizeFinder.prototype.chooseSize = function (button) {
    var buttons = Array.prototype.slice.call(
      this.steps.size.querySelectorAll('[data-size]')
    );
    this.setPressed(buttons, button);

    var key = button.dataset.size;

    if (key === '__custom') {
      this.state.sizeKey = null;
      this.state.custom = null;
      this.state.format = null;
      if (this.customPanel) {
        this.customPanel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        var w = this.customPanel.querySelector('[data-custom-width]');
        if (w) w.focus();
      }
      this.waitFormat();
      this.hideStep('results');
      return;
    }

    if (this.customPanel) {
      this.customPanel.hidden = true;
      var customBtn = this.steps.size.querySelector('[data-size="__custom"]');
      if (customBtn) customBtn.setAttribute('aria-expanded', 'false');
    }

    this.state.sizeKey = key;
    this.state.sizeLabel = button.dataset.sizeLabel || button.textContent.trim();
    this.state.custom = null;
    this.state.format = null;

    this.advanceToFormat();
    this.syncHash();
  };

  SizeFinder.prototype.submitCustom = function () {
    var wEl = this.customPanel.querySelector('[data-custom-width]');
    var hEl = this.customPanel.querySelector('[data-custom-height]');
    var w = num(wEl && wEl.value);
    var h = num(hEl && hEl.value);

    var error = null;
    var tooLarge = false;
    if (w === null || h === null) error = 'Enter both a width and a height.';
    else if (w <= 0 || h <= 0) error = 'Enter measurements greater than zero.';
    else if (w > 60 || h > 60) tooLarge = true;

    if (this.customError) {
      this.customError.textContent = error || '';
      this.customError.hidden = !error;
    }
    if (error) return;

    if (tooLarge) {
      this.showTooLarge(w, h);
      return;
    }

    this.state.custom = { w: w, h: h };
    this.state.sizeKey = null;
    this.state.sizeLabel = w + ' × ' + h + ' in';
    this.state.format = null;

    this.advanceToFormat();
    this.syncHash();
  };

  // Oversized entries go straight to the results area with the phone number
  // and the Can't Find the Perfect Fit block, instead of a dead-end error.
  SizeFinder.prototype.showTooLarge = function (w, h) {
    this.state.custom = { w: w, h: h };
    this.state.sizeKey = null;
    this.state.sizeLabel = w + ' × ' + h + ' in';
    this.state.format = null;

    this.cards.forEach(function (c) {
      c.hidden = true;
      c.style.order = '';
    });

    var phone = (this.root.dataset.phone || '').trim();
    this.summary.textContent = '';
    if (phone) {
      var link = document.createElement('a');
      link.href = 'tel:' + phone.replace(/[^0-9+]/g, '');
      link.textContent = phone;
      this.summary.appendChild(document.createTextNode('That looks too large. Give us a call at '));
      this.summary.appendChild(link);
      this.summary.appendChild(document.createTextNode(' and we will help.'));
    } else {
      this.summary.textContent = 'That looks too large. Give us a call and we will help.';
    }

    if (this.note) this.note.hidden = true;
    if (this.none) this.none.hidden = true;
    if (this.perfectFit) this.perfectFit.hidden = false;

    this.hideStep('format');
    this.showStep('results');
    this.scrollTo(this.steps.results);
    this.syncHash();
  };

  SizeFinder.prototype.advanceToFormat = function () {
    var count = this.buildFormatStep();

    if (count === 0) {
      // Nothing fits at all, so skip step 2 and go straight to the empty state.
      this.hideStep('format');
      this.renderResults();
      this.scrollTo(this.steps.results);
      return;
    }

    if (count === 1) {
      // Only one format available, so choosing it would be busywork.
      this.state.format = '__all';
      this.hideStep('format');
      this.renderResults();
      this.scrollTo(this.steps.results);
      return;
    }

    this.showStep('format');
    this.hideStep('results');
    this.scrollTo(this.steps.format);
  };

  SizeFinder.prototype.chooseFormat = function (button) {
    var buttons = Array.prototype.slice.call(
      this.steps.format.querySelectorAll('[data-format]')
    );
    this.setPressed(buttons, button);
    this.state.format = button.dataset.format;
    this.renderResults();
    this.scrollTo(this.steps.results);
    this.syncHash();
  };

  SizeFinder.prototype.reset = function () {
    this.state = { sizeKey: null, sizeLabel: null, custom: null, format: null };
    this.cards.forEach(function (c) {
      c.hidden = true;
      c.style.order = '';
    });
    this.setPressed(
      Array.prototype.slice.call(this.root.querySelectorAll('[data-size],[data-format]')),
      null
    );
    if (this.customPanel) this.customPanel.hidden = true;
    if (this.customError) this.customError.hidden = true;
    if (this.note) this.note.hidden = true;
    if (this.none) this.none.hidden = true;
    if (this.perfectFit) this.perfectFit.hidden = true;
    this.waitFormat();
    this.hideStep('results');
    this.scrollTo(this.steps.size);
    this.syncHash();
  };

  SizeFinder.prototype.scrollTo = function (el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    // Skip the animation for anyone who has asked for reduced motion.
    var reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
  };

  /* ---------- URL memory ---------- */

  // The selection lives in the URL hash, so a result can be linked to and the
  // back button steps back through the choices:
  //   #find-your-size                     nothing chosen (also the menu anchor)
  //   #find-your-size/14x17               size chosen
  //   #find-your-size/14x17/roll          results
  //   #find-your-size/custom-15x20/all    typed size results
  SizeFinder.prototype.hashFor = function () {
    var s = this.state;
    var parts = [HASH_BASE];
    if (s.custom) parts.push('custom-' + s.custom.w + 'x' + s.custom.h);
    else if (s.sizeKey) parts.push(s.sizeKey);
    if ((s.custom || s.sizeKey) && s.format) parts.push(s.format === '__all' ? 'all' : s.format);
    return '#' + parts.join('/');
  };

  SizeFinder.prototype.syncHash = function () {
    if (this.restoring || !window.history || !history.pushState) return;
    var h = this.hashFor();
    if (h === location.hash) return;
    // Nothing chosen and no finder hash in the URL yet: leave the URL alone.
    if (h === '#' + HASH_BASE && location.hash.indexOf('#' + HASH_BASE) !== 0) return;
    history.pushState(history.state, '', location.pathname + location.search + h);
  };

  SizeFinder.prototype.restore = function (hash) {
    hash = hash || '';
    if (hash === '' || hash === '#') hash = '#' + HASH_BASE;
    var m = /^#find-your-size(?:\/([^\/]+))?(?:\/([^\/]+))?$/.exec(hash);
    if (!m) return false;
    if (hash === this.hashFor()) return true;
    this.restoring = true;
    try {
      var size = m[1] ? decodeURIComponent(m[1]) : '';
      var fmt = m[2] ? decodeURIComponent(m[2]) : '';
      if (!size) {
        if (this.state.sizeKey || this.state.custom) this.reset();
        return true;
      }
      var c = /^custom-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/.exec(size);
      if (c) {
        var customBtn = this.steps.size.querySelector('[data-size="__custom"]');
        if (!customBtn || !this.customPanel) return true;
        this.chooseSize(customBtn);
        var wEl = this.customPanel.querySelector('[data-custom-width]');
        var hEl = this.customPanel.querySelector('[data-custom-height]');
        if (wEl) wEl.value = c[1];
        if (hEl) hEl.value = c[2];
        this.submitCustom();
      } else {
        var btn = null;
        Array.prototype.forEach.call(this.steps.size.querySelectorAll('[data-size]'), function (b) {
          if (b.dataset.size === size && size !== '__custom') btn = b;
        });
        if (!btn) return true;
        this.chooseSize(btn);
      }
      if (fmt && this.steps.format && !this.steps.format.hidden) {
        var key = fmt === 'all' ? '__all' : fmt;
        var fb = null;
        Array.prototype.forEach.call(this.steps.format.querySelectorAll('[data-format]'), function (b) {
          if (b.dataset.format === key && !b.hidden) fb = b;
        });
        if (fb) this.chooseFormat(fb);
      }
      return true;
    } finally {
      this.restoring = false;
    }
  };

  /* ---------- events ---------- */

  SizeFinder.prototype.bind = function () {
    var self = this;

    // Back and forward buttons walk through the selections.
    window.addEventListener('popstate', function () {
      self.restore(location.hash);
    });

    this.root.addEventListener('click', function (event) {
      var sizeBtn = event.target.closest('[data-size]');
      if (sizeBtn && self.root.contains(sizeBtn)) {
        self.chooseSize(sizeBtn);
        return;
      }

      var formatBtn = event.target.closest('[data-format]');
      if (formatBtn && self.root.contains(formatBtn)) {
        self.chooseFormat(formatBtn);
        return;
      }

      if (event.target.closest('[data-custom-submit]')) {
        self.submitCustom();
        return;
      }

      if (event.target.closest('[data-restart]')) {
        self.reset();
      }
    });

    // Enter in either custom field submits, rather than doing nothing.
    this.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.matches('[data-custom-width],[data-custom-height]')) return;
      event.preventDefault();
      self.submitCustom();
    });
  };

  /* ---------- boot ---------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-size-finder]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.sizeFinderReady) return;
      root.dataset.sizeFinderReady = 'true';
      new SizeFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // InstantClick swaps the body without a reload.
  document.addEventListener('instantclick:newpage', function () { init(); });
})();
